<template>
    <v-container>
        <page-header :title="`${mode === 'edit' ? 'Edit' : mode === 'show' ? '' : 'New'} Player`.trim()" icon="mdi-account-outline" />
        <v-card class="form-card" elevation="1">
            <v-form :readonly="readOnly">
                <v-row>
                    <v-col cols="12" md="8">
                        <div class="form-section-title">
                            <v-icon small>mdi-card-account-details-outline</v-icon>Personal info
                        </div>
                        <v-row>
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    v-model="player.name"
                                    outlined
                                    dense
                                    hide-details="auto"
                                    label="Name"
                                    :clearable="clearable"
                                />
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    v-model="player.surname"
                                    outlined
                                    dense
                                    hide-details="auto"
                                    label="Surname"
                                    :clearable="clearable"
                                />
                            </v-col>
                            <v-col cols="12" sm="6">
                                <date-time-picker
                                    :date-label="'Birthday'"
                                    :date="birthday"
                                    @setDate="setBirthday"
                                />
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    v-model="player.residence"
                                    outlined
                                    dense
                                    hide-details="auto"
                                    label="Residence"
                                    prepend-inner-icon="mdi-home-outline"
                                    :clearable="clearable"
                                />
                            </v-col>
                        </v-row>

                        <div class="form-section-title">
                            <v-icon small>mdi-phone-outline</v-icon>Contact
                        </div>
                        <v-row>
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    v-model="player.mobile"
                                    outlined
                                    dense
                                    hide-details="auto"
                                    label="Mobile"
                                    prepend-inner-icon="mdi-cellphone"
                                    maxlength="10"
                                    :clearable="clearable"
                                />
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    v-model="player.email"
                                    outlined
                                    dense
                                    hide-details="auto"
                                    label="Email"
                                    prepend-inner-icon="mdi-email-outline"
                                    :clearable="clearable"
                                />
                            </v-col>
                        </v-row>
                    </v-col>
                    <v-col cols="12" md="4">
                        <div class="form-section-title">
                            <v-icon small>mdi-image-outline</v-icon>Photo
                        </div>
                        <image-upload
                            v-model="image"
                            :src="imagePath"
                            label="Player image"
                            height="240"
                            :readonly="readOnly"
                            @change="changeImage"
                            @clear="clearImage"
                        />
                    </v-col>
                </v-row>

                <div class="form-section-title">
                    <v-icon small>mdi-basketball</v-icon>Team & role
                </div>
                <v-row>
                    <v-col cols="12" sm="6" md="4">
                        <v-autocomplete
                            v-model="player.team"
                            outlined
                            dense
                            hide-details="auto"
                            :items="teamOptions"
                            item-text="name"
                            menu-props="auto"
                            label="Team"
                            :clearable="!readOnly"
                            return-object
                        />
                    </v-col>
                    <v-col cols="6" sm="3" md="2">
                        <v-text-field
                            v-model="player.jersey"
                            outlined
                            dense
                            hide-details="auto"
                            :disabled="!player.active"
                            label="Jersey"
                            type="number"
                            :clearable="clearable"
                        />
                    </v-col>
                    <v-col cols="6" sm="3" md="2">
                        <v-text-field
                            v-model="player.height"
                            outlined
                            dense
                            hide-details="auto"
                            label="Height"
                            :clearable="clearable"
                        />
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                        <v-select
                            v-model="player.position"
                            outlined
                            dense
                            hide-details="auto"
                            :items="positions"
                            menu-props="auto"
                            label="Position"
                            :clearable="!readOnly"
                        />
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                        <v-text-field
                            v-model="player.athleteNumber"
                            outlined
                            dense
                            hide-details="auto"
                            label="Athlete Number"
                            type="number"
                            :clearable="clearable"
                        />
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                        <v-select
                            v-model="player.noOfMatches"
                            outlined
                            dense
                            hide-details="auto"
                            :items="['noOfMatches1', 'noOfMatches2', 'noOfMatches3']"
                            label="MatchesRule"
                            :clearable="!readOnly"
                        >
                            <template slot="selection" slot-scope="data">
                                <!-- HTML that describe how select should render selected items -->
                                {{ `#MatchesRule${data.item.slice(-1)}` }}
                            </template>
                            <template slot="item" slot-scope="data">
                                <!-- HTML that describe how select should render items when the select is open -->
                                {{ `#MatchesRule${data.item.slice(-1)}` }}
                            </template>
                        </v-select>
                    </v-col>
                </v-row>

                <div class="form-section-title">
                    <v-icon small>mdi-toggle-switch-outline</v-icon>Status
                </div>
                <v-row>
                    <v-col cols="6" md="3">
                        <v-switch
                            v-model="player.active"
                            class="mt-0 pt-0"
                            color="orange"
                            hide-details
                            inset
                            :label="player.active ? 'Active' : 'Not Active'"
                        />
                    </v-col>
                    <v-col cols="6" md="3">
                        <v-switch
                            v-model="player.leader"
                            class="mt-0 pt-0"
                            color="orange"
                            hide-details
                            inset
                            :label="player.leader ? 'Leader' : 'Regular'"
                        />
                    </v-col>
                    <v-col cols="6" md="3">
                        <v-switch
                            v-model="player.coach"
                            class="mt-0 pt-0"
                            color="orange"
                            hide-details
                            inset
                            :label="player.coach ? 'Coach' : 'Not Coach'"
                        />
                    </v-col>
                    <v-col cols="6" md="3">
                        <!-- with no team (player pool) only the available ones are in the free agents -->
                        <v-switch
                            v-model="player.available"
                            class="mt-0 pt-0"
                            color="orange"
                            hide-details
                            inset
                            :label="player.available ? 'Available (free agent)' : 'Not Available'"
                        />
                    </v-col>
                    <v-col cols="6" md="3">
                        <v-switch
                            v-model="player.doctorCheck"
                            class="mt-0 pt-0"
                            color="orange"
                            hide-details
                            inset
                            :label="`Doctor Check: ${player.doctorCheck ? 'Ok' : 'Pending'}`"
                        />
                    </v-col>
                </v-row>

                <div class="form-section-title">
                    <v-icon small>mdi-play-box-multiple-outline</v-icon>Highlights
                </div>
                <div v-if="!readOnly" class="highlight-input">
                    <v-text-field
                        v-model="highlightUrl"
                        outlined
                        dense
                        hide-details="auto"
                        label="Add Video Highlight URL"
                        prepend-inner-icon="mdi-link-variant"
                        type="text"
                        :clearable="clearable"
                    />
                    <v-btn
                        :disabled="!highlightUrl || !validURL(highlightUrl)"
                        class="ml-2 white--text"
                        depressed
                        color="black"
                        @click="addHighlight"
                    >
                        <v-icon left small>mdi-plus</v-icon>Add
                    </v-btn>
                </div>
                <v-chip-group class="hightlight-urls" column>
                    <div v-for="(highlight, index) of player.highlights" :key="index" class="mr-3">
                        <v-chip @click="openLink(highlight)">Highlight {{ index + 1 }}</v-chip>
                        <delete-button :type="false" :color="'red'" @delete-entry="onHighlightDelete(index)" />
                    </div>
                </v-chip-group>
                <div v-if="!player.highlights || !player.highlights.length" class="text-caption grey--text">
                    No highlights yet.
                </div>
            </v-form>
        </v-card>
        <v-row v-if="playerId" class="mt-4 mx-0">
            <player-penalties :player-id="playerId" :read-only="readOnly" />
        </v-row>
        <div v-if="mode !== 'show'" class="form-actions">
            <v-spacer />
            <v-btn large text @click="$router.go(-1)">
                Cancel
            </v-btn>
            <v-btn :loading="loading" :disabled="shouldDisableSubmit()" class="white--text" large depressed color="black" @click="onSubmit">
                <v-icon left>mdi-content-save-outline</v-icon>Save
            </v-btn>
        </div>
        <alert :show-alert="showAlert" :message="message" :type="type" />
    </v-container>
</template>

<script>
    import PageHeader from '@/components/PageHeader'
    import ImageUpload from '@/components/ImageUpload'
    import playerService from '@/services/player'
    import Alert from '@/components/Alert'
    import DateTimePicker from '@/components/DateTimePicker'
    import momentTimezone from 'moment-timezone'
    import teamService from '@/services/team'
    import DeleteButton from '@/components/DeleteButton'
    import PlayerPenalties from '@/components/PlayerPenalties'

    export default {
        name: 'PlayerForm',
        components: { Alert, DateTimePicker, DeleteButton, PlayerPenalties, PageHeader, ImageUpload },
        props: {
            propId: {
                type: Number,
                default: null
            },
            playerIndex: {
                type: Number,
                default: null
            }
        },
        data: () => {
            return {
                highlightUrl: '',
                showAlert: false,
                message: null,
                type: null,
                mode: null,
                readOnly: false,
                clearable: true,
                loading: false,
                birthday: null,
                googleApiHost: 'https://storage.googleapis.com',
                player: {
                    coach: false,
                    available: false
                },
                playerId: null,
                rules: [
                    v => !!v || 'Field is required'
                ],
                mobileRule: [ v => { if (v?.length < 10 && v?.length > 0) return 'Insert valid mobile number' } ],
                positions: ['guard', 'forward', 'center'],
                image: {
                    name: 'Image file'
                },
                imageChanged: false,
                imagePath: '',
                teams: []
            }
        },
        computed: {
            // Only active teams can be picked, but keep the player's current team so it still displays if inactive
            teamOptions() {
                const team = this.player.team
                return team && team.id && !this.teams.some(t => t.id === team.id) ? [team].concat(this.teams) : this.teams
            }
        },
        watch: {
            'player.height': {
                handler(value) {
                    this.player.height = value.replace(',', '.')
                }
            },
            'player.active': {
                handler(value) {
                    if (!value) {
                        this.player.jersey = null
                    }
                }
            },
            propId: {
                handler(value) {
                    this.playerId = this.propId
                    if (this.playerId) {
                        playerService.getPlayer(this.playerId).then(resp => {
                            this.player = resp.data
                            if (this.player.birthday) {
                                this.birthday = momentTimezone(this.player.birthday, 'DD/MM/YYYY').tz('Europe/Athens').format('YYYY-MM-DD').toString()
                                // this.birthday = this.player.birthday
                            }
                            if (this.player.image) {
                                this.imagePath = this.googleApiHost + '/' + this.player.image
                            }
                        })
                    }
                }
            }
        },
        async created() {
            this.mode = this.$route.params.mode
            this.fetchAllTeams()
            if (this.mode === 'show') {
                this.readOnly = true
                this.clearable = false
            }
            this.playerId = this.propId ? this.propId : this.$route.params.playerId
            if (this.playerId) {
                playerService.getPlayer(this.playerId).then(resp => {
                    this.player = resp.data
                    if (this.player.birthday) {
                        this.birthday = momentTimezone(this.player.birthday, 'DD/MM/YYYY').tz('Europe/Athens').format('YYYY-MM-DD').toString()
                        // this.birthday = this.player.birthday
                    }
                    if (this.player.image) {
                        this.imagePath = this.googleApiHost + '/' + this.player.image
                    }
                })
            }
        },
        methods: {
            onHighlightDelete(index) {
                this.player.highlights.splice(index, 1)
            },
            addHighlight() {
                if (!this.player.highlights) {
                    this.$set(this.player, 'highlights', [])
                }
                if (this.highlightUrl && this.validURL(this.highlightUrl)) {
                    this.player.highlights.push(this.highlightUrl)
                    this.highlightUrl = ''
                }
            },
            validURL(str) {
                const a = document.createElement('a')
                a.href = str
                return (a.host && a.host !== window.location.host)
            },
            openLink(url) {
                window.open(url, '_blank')
            },
            clearImage() {
                this.imagePath = ''
                this.player.image = null
            },
            changeImage() {
                this.imageChanged = true
                if (this.image) {
                    this.imagePath = URL.createObjectURL(this.image)
                }
            },
            setAlert(message, type) {
                this.showAlert = true
                this.message = message
                this.type = type
                setTimeout(() => { this.showAlert = false }, 7000)
            },
            shouldDisableSubmit() {
                return false
            },
            setBirthday(date) {
                this.birthday = momentTimezone(date).tz('Europe/Athens').format('YYYY-MM-DD').toString()
            },
            onEdit() {
                this.$router.push(`/players/${this.playerId}/edit`)
            },
            createPlayer() {
                this.loading = true
                playerService.createPlayer(this.player).then(playerResponse => {
                    if (playerResponse.id) {
                        this.playerId = playerResponse.id
                    }
                    this.loading = false
                    this.$router.push(`/players`)
                }).catch((error) => {
                    this.loading = false
                    this.setAlert(error.response.data.message, 'error')
                })
            },
            editPlayer() {
                this.loading = true
                playerService.updatePlayer(this.playerId, this.player).then(playerResponse => {
                    if (playerResponse.id) {
                        this.playerId = playerResponse.id
                    }
                    this.loading = false
                    if (this.propId) {
                        this.$emit('updatePlayer')
                    } else {
                        this.$router.push(`/players`)
                    }
                }).catch((error) => {
                    this.loading = false
                    this.setAlert(error.response.data.message, 'error')
                })
            },
            async onSubmit() {
                const uploadImageResponse = await this.uploadImage()
                const imageUrl = uploadImageResponse?.data.content || null
                if (!this.player.highlights?.length) {
                    delete this.player.highlights
                }
                if (imageUrl) {
                    this.player.image = imageUrl
                }
                if (this.birthday) {
                    this.player.birthday = momentTimezone(this.birthday).tz('Europe/Athens').format('DD/MM/YYYY').toString()
                }
                if (this.player.mobile) {
                    this.player.mobile = '+30' + this.player.mobile
                }
                if (this.mode === 'edit') {
                    this.editPlayer()
                } else if (this.mode === 'create') {
                    this.createPlayer()
                } else {
                    this.createPlayer()
                }
            },
            uploadImage() {
                if (!this.imageChanged || this.image == null) {
                    return
                }
                let formData = new FormData()
                formData.append('file', this.image)
                return playerService.uploadFile(formData)
            },
            fetchAllTeams() {
                teamService.getFilteredTeams({ active: true }).then(resp => {
                    this.teams = resp.data.content
                })
            }
        }

    }
</script>
<style>
.tox .tox-editor-header {
    z-index: 0 !important;
}
.tox .tox-statusbar {
    display: none !important;
}

.tox .tox-toolbar--scrolling {
    flex-wrap: wrap !important;
}
.tox.tox-tinymce.tox-platform-touch {
    height: 40vh !important;
    min-height: 350px;
}

</style>
<style scoped>
.v-chip {
    margin: 0 !important;
}

.choices {
    display: flex;
    justify-content: space-evenly;
}

.highlight-input {
    display: flex;
    align-items: center;
}

.hightlight-urls {
    padding: 1rem;
}

.body-label {
    color: rgba(0, 0, 0, 0.6);
}
.body-editor {
    margin-bottom: 2vh;
}
.edit-button-wrapper {
    width: 100%;
    display: flex;
    justify-content: flex-end;
}

.submit-button-wrapper {
    width: 100%;
    display: flex;
    justify-content: center;
    margin-top: 2vh;
}

.img-preview {
    max-width: 70%;
    margin: 0 auto;
}

.image-input {
    padding: 20px;
    width: 100%;
    border-style: groove;
    border: #D3D3D3 solid 2px;
    border-radius: 15px 50px;
    margin-top: 1vh;
}

.green-border {
    border-color: green;
}

.delete-btn {
    display: flex;
    justify-content: flex-end;
}
</style>
